import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AdesaoService } from '../../../../services/adesao/adesao.service';
import {
  ClienteAdesao,
  ComparativoAdesao,
  EnvioTermo,
  FamiliaAdesao,
  OpcaoAdesao,
  OrigemOferta,
  PagamentoAdesao,
  TermoAdesao,
} from '../../../../interfaces/adesao.dto';
import { BrlPipe } from '../../../../utils/brl.pipe';
import { termoLiberadoPara } from '../../../../utils/termo-liberado.util';
import { ERRO_CELULAR, ERRO_EMAIL, formatarDestino, validarCelular, validarEmail } from '../../../../utils/contato-termo.util';
import { BrandService } from '../../../../services/brand/brand.service';
import { AcoesCobranca, CobrancaSimComponent } from '../cobranca-sim/cobranca-sim.component';

type Etapa = 'oferta' | 'cobranca' | 'pago' | 'recusado';
type Canal = 'sms' | 'email';

/** Cor de cada família (handoff v3, seção 4). Ouro usa texto escuro: branco no amarelo não passa em contraste. */
export const CORES_PLANO: Record<string, { fundo: string; texto: string; sombra: string }> = {
  platina: { fundo: '#7A3BA8', texto: '#FFFFFF', sombra: 'rgba(122,59,168,.8)' },
  black:   { fundo: '#101820', texto: '#FFFFFF', sombra: 'rgba(16,24,32,.8)' },
  ouro:    { fundo: '#E2AE2C', texto: '#3A2A03', sombra: 'rgba(184,134,11,.8)' },
};

/** O que o step 3 precisa saber quando o paciente vira assinante. */
export interface AssinaturaAtivada {
  cdConvenio: number | null;
  cdCategoria: number | null;
  familia: FamiliaAdesao;
  comPlano: number | null;
}

/**
 * Oferta SiM+ do step 3 (handoff v3). Só aparece para particular sem assinatura, com
 * operador que vende. Nada aqui impede o atendimento: sem cobrança aprovada, ele é criado
 * como particular.
 */
@Component({
  selector: 'app-oferta-sim',
  standalone: true,
  imports: [CommonModule, FormsModule, BrlPipe, CobrancaSimComponent],
  templateUrl: './oferta-sim.component.html',
  styleUrl: './oferta-sim.component.scss',
})
export class OfertaSimComponent implements OnChanges, OnDestroy {
  @Input() clinicId = 0;
  @Input() personId = 0;
  @Input() cpf = '';
  @Input() convenioParticular = false;
  @Input() procedimentos: { cdProcedimento: number; quantidade: number }[] = [];
  @Input() guiche = '';
  @Input() senhaId = 0;
  /** Fora do atendimento (Venda Cartão) não há visita para comparar com e sem plano. */
  @Input() origem: OrigemOferta = null;

  /** Total particular da visita, para o card do paciente mostrar antes da oferta. */
  @Output() valorParticular = new EventEmitter<number | null>();
  @Output() ativada = new EventEmitter<AssinaturaAtivada>();

  elegivel = false;
  carregando = false;

  // Frases da espera: a vitrine vem do CartaoSiM e pode levar alguns segundos.
  get frasesCarregando(): string[] {
    return [
      'Buscando as melhores condições de planos...',
      'Calculando quanto o paciente economiza hoje...',
      'Comparando Platina, Black e Ouro...',
      `Preparando a oferta ${this.nomePlano}...`,
    ];
  }
  fraseAtual = 0;
  private girarFrases: any = null;
  familias: FamiliaAdesao[] = [];
  comparativos: ComparativoAdesao[] = [];
  cliente: ClienteAdesao | null = null;

  idx = 0;
  recusados = new Set<number>();
  etapa: Etapa = 'oferta';
  ciclo: 'mensal' | 'anual' = 'mensal';

  termo: TermoAdesao | null = null;
  termoEnviadoPara: { canal: Canal; destino: string; em: Date; planoId: number } | null = null;
  canalEscolhido: Canal = 'sms';
  isEnviandoTermo = false;
  isImportandoTermo = false;
  readonly TAMANHO_MAX_TERMO = 10 * 1024 * 1024;

  pagamento: PagamentoAdesao | null = null;
  // Modelo da maquininha da ultima cobranca; vazio quando foi PIX.
  pagoNa = '';

  erro = '';
  baixandoModelo = false;
  private subscriptionId: number | null = null;

  // Os mesmos dias que o back aceita (AdesaoPayload.DiaDeVencimentoValido).
  readonly DIAS_VENCIMENTO = [5, 10, 15];
  diaVencimento = 10;

  // Contato corrigido na hora vale so para o envio do termo; o cadastro nao muda.
  contatoEditado: Partial<Record<Canal, string>> = {};
  editandoCanal: Canal | null = null;
  valorEdicao = '';
  erroEdicao = '';

  // Assinatura acontece no celular do paciente: a tela consulta sozinha ate liberar.
  private timerTermo: any = null;
  private termoAte = 0;
  verificandoTermo = false;

  constructor(private adesaoService: AdesaoService, private brandService: BrandService) {}

  // "SiM+" na Clinica SiM, "Super Vida" na Super Clinica.
  get nomePlano(): string {
    return this.brandService.nomePlano;
  }

  // Nome da familia sem o prefixo da marca ("SiM+ Black" -> "Black").
  nomeCurto(nome: string): string {
    return (nome ?? '').replace(/^(SiM\+|Super Vida)\s+/i, '');
  }

  ngOnChanges(changes: SimpleChanges): void {
    // A propria ativacao troca o convenio do pai (particular -> plano): recarregar aqui
    // esconderia a confirmacao "agora e assinante".
    if (!changes['personId'] && this.etapa === 'pago') return;
    if (changes['personId'] || changes['convenioParticular']) this.carregar();
  }

  ngOnDestroy(): void {
    this.pararVerificacaoTermo();
    this.pararCarregamento();
  }

  private iniciarCarregamento(): void {
    this.carregando = true;
    this.fraseAtual = 0;
    this.girarFrases = setInterval(() => {
      this.fraseAtual = (this.fraseAtual + 1) % this.frasesCarregando.length;
    }, 1800);
  }

  private pararCarregamento(): void {
    this.carregando = false;
    if (this.girarFrases) clearInterval(this.girarFrases);
    this.girarFrases = null;
  }

  // ── Carga ───────────────────────────────────────────────────

  private async carregar(): Promise<void> {
    this.pararVerificacaoTermo();
    this.contatoEditado = {};
    this.editandoCanal = null;
    this.diaVencimento = 10;
    this.elegivel = false;
    this.etapa = 'oferta';
    this.recusados.clear();
    this.subscriptionId = null;
    this.pagamento = null;
    this.erro = '';

    const cpf = (this.cpf ?? '').replace(/\D/g, '');
    if (!this.personId || !this.clinicId) return;

    // Elegibilidade e comparativo sao leitura local (Oracle): juntos e rapidos.
    const [comparativos, elegibilidade] = await Promise.all([
      this.origem ? Promise.resolve([]) : this.adesaoService.comparativo(this.clinicId, this.procedimentos),
      this.adesaoService.elegibilidade(this.clinicId, this.personId, this.convenioParticular, cpf),
    ]);
    this.comparativos = comparativos;
    this.valorParticular.emit(comparativos[0]?.semPlano ?? null);
    if (!elegibilidade.pode) return;

    // Vitrine, cliente e termo vem do CartaoSiM: em paralelo, com aviso na tela.
    this.iniciarCarregamento();
    const termo = this.adesaoService.termo(cpf).then(t => (this.termo = t));
    const [familias, cliente] = await Promise.all([
      this.adesaoService.vitrine(this.clinicId),
      this.adesaoService.cliente(cpf),
    ]);
    this.pararCarregamento();
    if (!familias.length) return;

    this.familias = familias;
    this.cliente = cliente;
    this.canalEscolhido = this.canalPadrao;
    this.selecionarPlano(0, false);
    this.elegivel = true;
    this.registrar('oferta_exibida');
    await termo;
    this.retomarVerificacaoTermo();
  }

  // Termo ja enviado (outra aba, antes do F5): segue esperando a assinatura como se fosse desta aba.
  private retomarVerificacaoTermo(): void {
    if (this.envioAnterior && !this.termoLiberado) this.iniciarVerificacaoTermo();
  }

  // ── Plano e ciclo ────────────────────────────────────────────

  get familia(): FamiliaAdesao | null {
    return this.familias[this.idx] ?? null;
  }

  get opcao(): OpcaoAdesao | null {
    const f = this.familia;
    if (!f) return null;
    return (this.ciclo === 'anual' ? f.anual : f.mensal) ?? f.mensal ?? f.anual;
  }

  get comparativo(): ComparativoAdesao | null {
    return this.comparativos.find(c => c.familia === this.familia?.familia) ?? null;
  }

  get cor() {
    return CORES_PLANO[this.familia?.familia ?? ''] ?? CORES_PLANO['platina'];
  }

  corDe(familia: FamiliaAdesao) {
    return CORES_PLANO[familia.familia] ?? CORES_PLANO['platina'];
  }

  selecionarPlano(i: number, registrar = true): void {
    const trocou = i !== this.idx;
    this.idx = i;
    this.ciclo = this.familias[i]?.mensal ? 'mensal' : 'anual';
    this.erro = '';
    // Trocar de plano é outro documento: o termo enviado era do plano anterior.
    if (trocou) {
      this.termoEnviadoPara = null;
      this.pararVerificacaoTermo();
      this.retomarVerificacaoTermo();
    }
    if (registrar && trocou) this.registrar('plano_trocado');
  }

  get nomesDosPlanos(): string {
    return this.familias.map(p => this.nomeCurto(p.nome)).join(', ');
  }

  get proximoNaoRecusado(): number | null {
    for (let i = this.idx + 1; i < this.familias.length; i++) if (!this.recusados.has(i)) return i;
    return null;
  }

  recusar(): void {
    this.registrar('oferta_recusada');
    this.recusados.add(this.idx);
    const proximo = this.proximoNaoRecusado;
    if (proximo === null) {
      this.etapa = 'recusado';
      return;
    }
    // O relatório só sabe que o plano seguinte foi ofertado por este evento.
    this.selecionarPlano(proximo);
  }

  desfazerRecusa(): void {
    this.registrar('oferta_desfeita');
    this.recusados.clear();
    this.etapa = 'oferta';
    this.selecionarPlano(0, false);
  }

  // ── Termo ───────────────────────────────────────────────────

  readonly canais: { id: Canal; nome: string; semContato: string }[] = [
    { id: 'sms', nome: 'SMS', semContato: 'Sem celular no cadastro' },
    { id: 'email', nome: 'E-mail', semContato: 'Sem e-mail no cadastro' },
  ];

  // So o termo assinado libera a cobranca (decisao de 25/09/2026; antes bastava enviar).
  // A consulta de assinatura e so por CPF: sozinha, ela liberaria o termo de outro plano
  // ou de outra visita. Por isso so vale assinatura posterior a um envio do plano escolhido
  // (desta aba ou gravado no back nas 24h; 5 min de folga de relogio), ou PDF importado do plano.
  get termoLiberado(): boolean {
    return termoLiberadoPara(this.termo, this.opcao?.planoId, this.termoEnviadoPara);
  }

  private get enviosDoPlano(): EnvioTermo[] {
    const planoId = this.opcao?.planoId;
    return (this.termo?.envios ?? []).filter(e => e.planoId === planoId);
  }

  get termoImportado(): EnvioTermo | null {
    return this.enviosDoPlano.find(e => e.canal === 'importado') ?? null;
  }

  // Envio gravado no back (outra aba ou antes de recarregar), para a tela nao parecer "nunca enviado".
  get envioAnterior(): EnvioTermo | null {
    return this.enviosDoPlano.find(e => e.canal !== 'importado') ?? null;
  }

  async baixarTermoModelo(): Promise<void> {
    const planoId = this.opcao?.planoId;
    if (this.baixandoModelo || !planoId) return;
    this.baixandoModelo = true;
    this.erro = '';
    try {
      await this.adesaoService.baixarTermoModelo(planoId, this.clinicId);
    } catch (e: any) {
      this.erro = e.message;
    } finally {
      this.baixandoModelo = false;
    }
  }

  async importarTermo(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const arquivo = input.files?.[0];
    input.value = '';
    const opcao = this.opcao;
    if (!arquivo || !opcao || this.isImportandoTermo) return;

    const ehPdf = arquivo.type === 'application/pdf' || arquivo.name.toLowerCase().endsWith('.pdf');
    if (!ehPdf) {
      this.erro = 'Selecione o termo assinado em PDF.';
      return;
    }
    if (arquivo.size > this.TAMANHO_MAX_TERMO) {
      this.erro = 'O PDF passa de 10 MB.';
      return;
    }

    this.isImportandoTermo = true;
    this.erro = '';
    try {
      this.termo = await this.adesaoService.importarTermo(
        (this.cpf ?? '').replace(/\D/g, ''), opcao.planoId, this.clinicId, arquivo);
      if (this.termoLiberado) this.pararVerificacaoTermo();
    } catch (e: any) {
      this.erro = e?.message ?? 'Não foi possível importar o termo.';
    } finally {
      this.isImportandoTermo = false;
    }
  }

  get canalPadrao(): Canal {
    return this.cliente?.telefone ? 'sms' : 'email';
  }

  destinoDe(canal: Canal): string {
    return this.contatoEditado[canal] ?? (canal === 'sms' ? this.cliente?.telefone : this.cliente?.email) ?? '';
  }

  editarContato(canal: Canal): void {
    this.editandoCanal = canal;
    this.canalEscolhido = canal;
    this.valorEdicao = canal === 'sms' ? this.destinoDe(canal).replace(/\D/g, '') : this.destinoDe(canal);
    this.erroEdicao = '';
  }

  salvarContato(): void {
    const canal = this.editandoCanal;
    if (!canal) return;
    if (canal === 'sms') {
      const d = validarCelular(this.valorEdicao);
      if (!d) {
        this.erroEdicao = ERRO_CELULAR;
        return;
      }
      this.contatoEditado.sms = d;
    } else {
      const email = validarEmail(this.valorEdicao);
      if (!email) {
        this.erroEdicao = ERRO_EMAIL;
        return;
      }
      this.contatoEditado.email = email;
    }
    this.editandoCanal = null;
    this.erroEdicao = '';
  }

  cancelarEdicao(): void {
    this.editandoCanal = null;
    this.erroEdicao = '';
  }

  contatoFoiEditado(canal: Canal): boolean {
    return this.contatoEditado[canal] !== undefined;
  }

  // Consulta a cada 15s por ate 15 min depois do envio; o botao "Verificar" consulta na hora.
  private iniciarVerificacaoTermo(): void {
    this.pararVerificacaoTermo();
    this.termoAte = Date.now() + 15 * 60 * 1000;
    this.timerTermo = setInterval(() => {
      if (Date.now() > this.termoAte || this.termoLiberado) {
        this.pararVerificacaoTermo();
        return;
      }
      this.verificarTermo(false);
    }, 15_000);
  }

  private pararVerificacaoTermo(): void {
    if (this.timerTermo) clearInterval(this.timerTermo);
    this.timerTermo = null;
  }

  async verificarTermo(manual = true): Promise<void> {
    const cpf = (this.cpf ?? '').replace(/\D/g, '');
    if (this.verificandoTermo || !cpf) return;
    this.verificandoTermo = true;
    try {
      const t = await this.adesaoService.termo(cpf);
      if (t) this.termo = t;
      if (this.termoLiberado) {
        this.pararVerificacaoTermo();
        this.registrar('termo_assinado');
      } else if (manual) {
        this.erro = '';
      }
    } catch {
      if (manual) this.erro = 'Não foi possível consultar a assinatura agora.';
    } finally {
      this.verificandoTermo = false;
    }
  }

  formatarDestino(canal: Canal, destino: string): string {
    return formatarDestino(canal, destino);
  }

  async enviarTermo(canal: Canal): Promise<void> {
    const opcao = this.opcao;
    const destino = this.destinoDe(canal);
    if (!opcao || this.isEnviandoTermo) return;
    if (!destino) {
      this.erro = canal === 'sms' ? 'Paciente sem celular no cadastro.' : 'Paciente sem e-mail no cadastro.';
      return;
    }

    this.isEnviandoTermo = true;
    this.erro = '';
    try {
      this.termo = await this.adesaoService.enviarTermo({
        cpf: this.cpf, planoId: opcao.planoId, planoNome: opcao.nomePlano,
        paymentMethodCode: 'terminal', clinicId: this.clinicId, canal, destino,
      });
      this.termoEnviadoPara = { canal, destino, em: new Date(), planoId: opcao.planoId };
      this.registrar('termo_enviado');
      if (!this.termoLiberado) this.iniciarVerificacaoTermo();
    } catch (e: any) {
      this.erro = e?.message ?? 'Não foi possível enviar o termo.';
    } finally {
      this.isEnviandoTermo = false;
    }
  }

  // ── Cobrança ────────────────────────────────────────────────

  irParaCobranca(): void {
    if (!this.termoLiberado) return;
    // Voltou e trocou plano ou ciclo: a assinatura ja criada era de outro plano.
    if (this.assinaturaDe && !this.assinaturaDe.startsWith(`${this.opcao?.planoId}:`)) this.esquecerAssinatura();
    this.etapa = 'cobranca';
    this.erro = '';
  }

  voltarParaOferta(): void {
    this.etapa = 'oferta';
  }

  get valorCobranca(): number {
    return this.pagamento?.valor ?? this.opcao?.valor ?? 0;
  }

  // "planoId:formaId" da assinatura criada; outra combinacao exige outra assinatura.
  private assinaturaDe: string | null = null;

  private esquecerAssinatura(): void {
    this.subscriptionId = null;
    this.assinaturaDe = null;
    this.pagamento = null;
  }

  // PIX so no anual: o back ja tira o PIX da opcao mensal, a tela so obedece.
  get aceitaPix(): boolean {
    return !!this.opcao?.formasPagamento.some(f => f.codigo === 'pix');
  }

  get temAssinatura(): boolean {
    return !!this.subscriptionId;
  }

  private formaDoMeio(codigo: 'terminal' | 'pix'): { id: number; codigo: string } | null {
    const forma = this.opcao?.formasPagamento.find(f => f.codigo === codigo);
    return forma ? { id: forma.id, codigo } : null;
  }

  // Cria a assinatura na hora de cobrar: a forma de pagamento faz parte dela.
  private async garantirAssinatura(codigo: 'terminal' | 'pix'): Promise<number> {
    const opcao = this.opcao;
    const forma = this.formaDoMeio(codigo);
    if (!opcao || !forma) throw new Error(codigo === 'pix' ? 'Este plano não aceita PIX.' : 'Este plano não aceita maquininha.');
    const chave = `${opcao.planoId}:${forma.id}`;
    if (this.subscriptionId && this.assinaturaDe === chave) return this.subscriptionId;
    if (this.subscriptionId) this.esquecerAssinatura();

    const resultado = await this.adesaoService.criarAssinatura({
      cpf: this.cpf, personId: this.personId, planoId: opcao.planoId,
      paymentMethodId: forma.id, paymentMethodCode: forma.codigo, establishmentId: this.clinicId,
      diaVencimento: this.ciclo === 'mensal' && opcao.aceitaVencimento ? this.diaVencimento : null,
      origem: this.origem,
    });
    this.subscriptionId = resultado.subscriptionId;
    this.assinaturaDe = chave;
    this.pagamento = await this.adesaoService.cobranca(resultado.subscriptionId);
    this.registrar('cobranca_iniciada');
    return this.subscriptionId;
  }

  readonly acoesAdesao: AcoesCobranca = {
    cobrarNoTerminal: async poiid => {
      const id = await this.garantirAssinatura('terminal');
      await this.adesaoService.cobrarNoTerminal(id, poiid, 1, this.clinicId);
      this.pagoNa = poiid.split('-')[0];
      this.pagoNoCheckout = false;
    },
    gerarLink: async () => {
      const id = await this.garantirAssinatura('pix');
      const link = await this.adesaoService.linkPagamento(id);
      this.pagamento = { ...link, valor: this.pagamento?.valor ?? link.valor };
      this.pagoNa = '';
      this.pagoNoCheckout = false;
      return this.pagamento;
    },
    enviarLink: (url, email, whatsApp) =>
      this.adesaoService.enviarLink(this.subscriptionId!, this.cliente!.id!, url, this.clinicId, email, whatsApp),
    foiPago: () => this.verificarAtivacao(),
    // O checkout cria a propria assinatura: aqui nao passa pelo garantirAssinatura.
    gerarCheckout: async (canal, destinoInformado) => {
      const opcao = this.opcao;
      if (!opcao) throw new Error('Escolha um plano.');
      const destino = destinoInformado
        || (canal === 'email' ? this.destinoDe('email') : canal === 'whatsapp' ? this.destinoDe('sms') : '');
      const resultado = await this.adesaoService.checkoutLink({
        cpf: (this.cpf ?? '').replace(/\D/g, ''), clinicId: this.clinicId, planoId: opcao.planoId,
        diaVencimento: this.ciclo === 'mensal' && opcao.aceitaVencimento ? this.diaVencimento : null,
        canal, destino: destino || undefined,
      });
      this.pagoNa = '';
      this.pagoNoCheckout = true;
      return resultado;
    },
    // Rota propria: cria a assinatura ja cobrando o cartao; o garantirAssinatura nao entra.
    pagarComCartao: async dados => {
      const opcao = this.opcao;
      if (!opcao?.cartaoPaymentMethodId) throw new Error('Este plano não aceita cartão de crédito.');
      const resultado = await this.adesaoService.assinaturaCartao({
        cpf: (this.cpf ?? '').replace(/\D/g, ''), personId: this.personId, planoId: opcao.planoId,
        paymentMethodId: opcao.cartaoPaymentMethodId, establishmentId: this.clinicId,
        diaVencimento: this.ciclo === 'mensal' && opcao.aceitaVencimento ? this.diaVencimento : null,
        parcelas: dados.parcelas, origem: this.origem,
        cartao: {
          holderName: dados.holderName, brand: dados.brand, checkoutAttemptId: dados.checkoutAttemptId ?? '',
          ...dados.encrypted,
        },
      });
      this.subscriptionId = resultado.subscriptionId;
      this.assinaturaDe = `${opcao.planoId}:${opcao.cartaoPaymentMethodId}`;
      this.pagoNa = '';
      this.pagoNoCheckout = true;
      this.registrar('cobranca_iniciada');
    },
  };

  pagoNoCheckout = false;

  onMaquininhaSemResposta(): void {
    this.registrar('cobranca_falhou');
  }

  // ── Ativação ────────────────────────────────────────────────

  // Quem ativa é o pagamento; a Fila só lê o resultado no banco.
  async verificarAtivacao(): Promise<boolean> {
    const status = await this.adesaoService.ativacao(this.personId);
    if (status.situacao !== 'ativo' || !this.familia) return false;

    this.etapa = 'pago';
    this.registrar('assinatura_contratada');
    this.ativada.emit({
      cdConvenio: status.cdConvenio, cdCategoria: status.cdCategoria,
      familia: this.familia, comPlano: this.comparativo?.comPlano ?? null,
    });
    return true;
  }

  // ── Relatório de conversão ─────────────────────────────────

  private registrar(tipo: string): void {
    this.adesaoService.registrarEvento(tipo, this.familia?.familia ?? '', this.personId, this.clinicId, this.senhaId, this.origem);
  }
}
